"use client";

import type { CalendarConfig } from "@/features/calendar/types/calendar-config";
import type { ExtendedTemplateSlot, TemplateType } from "@/features/calendar/types/templates";

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "motion/react";
import { toast, toast as sonner } from "sonner";
import { Icon } from "@iconify-icon/react";

import { useHostingOptions } from "../../types/hostingOptions.tsx";

import { TemplateSlotDetailsDialog } from "./TemplateSlotDetailsDialog";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { parseTime } from "@/features/calendar/utils/date";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/features/calendar/components/create-slot/createSlotAlert.tsx";
import { useTemplatesStore } from "@/features/calendar/store/useTemplatesStore";
import { API_ENDPOINTS } from "@/constants/api";
import { axiosApiInstance } from "@/libs/axios";
import { LessonHost } from "@/features/calendar/types/create-slot.ts";
import AppLogoIcon from "@/components/LogoIcon.tsx";

interface TemplateSlotComponentProps {
  event: ExtendedTemplateSlot;
  dayIndex: number;
  weekDaysLength: number;
  finalConfig: CalendarConfig;
  templateId: string;
  templateType: TemplateType;
  onSlotDeleted?: () => void;
  onEditSlot?: (slot: any) => void;
}

export function TemplateSlotComponent({
  event,
  finalConfig,
  templateId,
  templateType,
  onSlotDeleted,
  onEditSlot,
}: TemplateSlotComponentProps) {
  const { t } = useTranslation(["common", "calendar"]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(false);
  const { hourHeight, minHour, maxHour } = finalConfig;
  const { fetchTemplateSlots } = useTemplatesStore();
  const hostingOptions = useHostingOptions();

  // Use the same time parsing logic as in the calendar
  const [startHour, startMinute] = parseTime(event.startTime);
  const [endHour, endMinute] = parseTime(event.endTime);

  const start = startHour + startMinute / 60;
  let end = endHour + endMinute / 60;

  // Handle midnight crossing - if end time is earlier than start time, add 24 hours
  if (end < start) {
    end += 24;
  }

  const height = Math.min((end - start) * hourHeight, (maxHour - minHour) * hourHeight) - 4;
  const top = (start - minHour) * hourHeight + (height <= hourHeight / 2 ? 0 : 3);

  // Calculate slot duration in minutes
  const slotDurationMinutes = (end - start) * 60;

  // Check if this is a short slot (less than 30 minutes)
  const isShortSlot = slotDurationMinutes < 30;
  // Check if this is a medium slot (less than 45 minutes) - only show title and content for 45+ minutes
  const isMediumSlot = slotDurationMinutes < 45;

  // Get hosting method display info
  const getHostDisplayInfo = (lessonHost?: string) => {
    if (!lessonHost) return null;

    // Map the lesson_host value to LessonHost enum
    let mappedHost: LessonHost;

    if (lessonHost === "our_platform") {
      mappedHost = LessonHost.Ximly;
    } else if (lessonHost === "google_meets") {
      mappedHost = LessonHost.GoogleMeets;
    } else if (lessonHost === "zoom") {
      mappedHost = LessonHost.Zoom;
    } else {
      mappedHost = LessonHost.OtherWay;
    }

    const option = hostingOptions.find((opt) => opt.id === mappedHost);

    if (!option) return null;

    let icon;
    let shortName;

    if (mappedHost === LessonHost.Ximly) {
      icon = <AppLogoIcon className="size-3" />;
      shortName = "Ximly";
    } else if (mappedHost === LessonHost.GoogleMeets) {
      icon = <Icon height="0.75rem" icon="logos:google-meet" width="0.75rem" />;
      shortName = "Meet";
    } else if (mappedHost === LessonHost.Zoom) {
      icon = <Icon height="0.75rem" icon="logos:zoom" width="0.75rem" />;
      shortName = "Zoom";
    } else {
      icon = <Icon height="0.75rem" icon="ph:globe" width="0.75rem" />;
      shortName = "Other";
    }

    return {
      icon,
      shortName,
      fullTitle: option.title,
    };
  };

  const hostInfo = getHostDisplayInfo(event.lessonHost as LessonHost);

  // Handle slot deletion
  const handleDeleteSlot = async () => {
    try {
      setIsDeleting(true);
      // Call the API to delete the slot
      await axiosApiInstance.delete(
        `${API_ENDPOINTS.TUTOR.TEMPLATES.LIST}/${templateId}/slots/${event.id}`,
      );

      sonner.success(t("calendar:templates.slotDeleted"), {
        description: t("calendar:templates.slotDeletedDescription"),
      });

      // Refresh slots after deletion
      fetchTemplateSlots(templateId);

      if (onSlotDeleted) {
        onSlotDeleted();
      }
    } catch (error) {
      toast(t("common:error"), {
        description: t("calendar:templates.failedToDeleteSlot"),
      });
    } finally {
      setIsDeleting(false);
      setIsDeleteDialogOpen(false);
    }
  };

  return (
    <motion.div
      animate={{ opacity: 1 }}
      className="group bg-foreground/75 border-l-accent-foreground/75 absolute right-0.5 left-0.5 z-20 !w-[95%] cursor-pointer rounded-sm border border-l-4 shadow-sm transition-all hover:z-25 hover:scale-[1.01] hover:shadow-md"
      exit={{ opacity: 0 }}
      initial={{ opacity: 0 }}
      style={{
        top: `${Math.max(top, 0)}px`,
        height: isMediumSlot
          ? `${(end - start) * hourHeight}px`
          : `${Math.max(height, hourHeight / 1.5) - 3}px`,
      }}
      onClick={(e) => {
        e.stopPropagation();
        if (!isDetailsDialogOpen && !isDeleteDialogOpen) {
          setIsDropdownOpen(!isDropdownOpen);
        }
      }}
    >
      {/* Dropdown menu - only show for slots 45+ minutes */}
      {!isMediumSlot && (
        <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
          <DropdownMenuTrigger asChild>
            <Button
              className="hover:bg-accent/50 hover:text-accent-foreground text-muted-foreground ring-none! absolute top-1 right-1 h-6 w-6 bg-transparent p-0 opacity-0 transition duration-200 outline-none group-hover:opacity-100 focus:ring-0 focus:ring-offset-0 focus-visible:ring-0! focus-visible:ring-offset-0 focus-visible:outline-none!"
              onClick={(e) => e.stopPropagation()}
            >
              <Icon height="1rem" icon="tabler:dots" width="1rem" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="bg-card w-[250px]" side="right">
            <DropdownMenuItem
              className="hover:bg-accent hover:text-accent-foreground flex items-center gap-2"
              onClick={() => {
                setIsDropdownOpen(false);
                setIsDetailsDialogOpen(true);
              }}
            >
              <Icon height="1rem" icon="tabler:info-circle" width="1rem" />
              <div className="flex flex-col">
                <span className="flex items-center gap-2">
                  {t("calendar:templates.viewDetails")}
                </span>
                <span className="text-muted-foreground text-xs">
                  {t("calendar:templates.viewSlotDetails")}
                </span>
              </div>
            </DropdownMenuItem>
            <DropdownMenuSeparator className="bg-muted-foreground/20" />
            <DropdownMenuItem
              className="hover:bg-accent hover:text-accent-foreground flex items-center gap-2"
              onClick={() => {
                setIsDropdownOpen(false);
                if (onEditSlot) {
                  onEditSlot(event.templateSlot);
                }
              }}
            >
              <Icon height="1rem" icon="tabler:pencil" width="1rem" />
              <div className="flex flex-col">
                <span className="flex items-center gap-2">{t("calendar:templates.editSlot")}</span>
                <span className="text-muted-foreground text-xs">
                  {t("calendar:templates.modifySlotSettings")}
                </span>
              </div>
            </DropdownMenuItem>
            <DropdownMenuSeparator className="bg-muted-foreground/20" />
            <DropdownMenuItem
              className="hover:bg-accent hover:text-accent-foreground"
              onClick={() => {
                setIsDropdownOpen(false);
                setIsDeleteDialogOpen(true);
              }}
            >
              <div className="flex items-center gap-2 text-red-500 focus:text-red-500">
                <Icon height="1rem" icon="tabler:trash" width="1rem" />
                <div className="flex flex-col">
                  <span className="flex items-center gap-2">
                    {t("calendar:templates.deleteSlot")}
                  </span>
                  <span className="text-muted-foreground text-xs">
                    {t("calendar:templates.deleteSlotDescription")}
                  </span>
                </div>
              </div>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {/* Content for short slots - only time */}
      {isShortSlot ? (
        <div className="flex h-full items-center justify-between px-2">
          <div className="text-muted-foreground text-[8px]">
            {`${event.startTime.slice(0, 5)} - ${event.endTime.slice(0, 5)}`}
          </div>
        </div>
      ) : isMediumSlot ? (
        <div className="flex h-full items-center justify-between px-2">
          <div className="text-muted-foreground text-xs">
            {`${event.startTime.slice(0, 5)} - ${event.endTime.slice(0, 5)}`}
          </div>
        </div>
      ) : (
        <div className="flex h-full flex-col p-2">
          <div className="flex flex-col">
            {/* Time with hosting method */}
            <div className="flex items-center gap-1">
              <div className="text-muted-foreground flex flex-col gap-0.5 text-xs">
                {`${event.startTime.slice(0, 5)} - ${event.endTime.slice(0, 5)}`}
                {hostInfo && height >= hourHeight / 1.2 && (
                  <>
                    <span className="inline-flex items-center gap-1" title={hostInfo.fullTitle}>
                      {hostInfo.icon}
                      <span>{hostInfo.shortName}</span>
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {isDetailsDialogOpen && (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <div onClick={(e) => e.stopPropagation()}>
          <TemplateSlotDetailsDialog
            isOpen={isDetailsDialogOpen}
            slotId={event.id}
            templateId={templateId}
            templateType={templateType}
            onClose={() => setIsDetailsDialogOpen(false)}
          />
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("calendar:templates.deleteSlotConfirmation")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("calendar:templates.deleteSlotWarning")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>{t("common:cancel")}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
              disabled={isDeleting}
              onClick={(e) => {
                e.preventDefault();
                handleDeleteSlot();
              }}
            >
              {isDeleting ? (
                <div className="flex items-center">
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  {t("common:deleting")}
                </div>
              ) : (
                t("common:delete")
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </motion.div>
  );
}
